import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '~/store'
import { addDirHandle, removeDirHandle, selectAllHandlesWithHashes } from '~/store/dirHandleSlice'
import { FileHandleStore, StoredHandle } from '~/utils/FileHandleStore'

export function useLocalRepositories() {
  const handleIds = useSelector((state: RootState) => selectAllHandlesWithHashes(state))

  const [storedHandles, setStoredHandles] = useState<StoredHandle[]>([])
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(null)
  const [processingLocalAction, setProcessingLocalAction] = useState<boolean>(false)

  const fileHandleStore = useMemo(() => new FileHandleStore(), [])

  // Load stored handles when handle IDs change
  useEffect(() => {
    const loadHandles = async () => {
      try {
        await fileHandleStore.init()
        const loadedHandles = await Promise.all(
          handleIds.map(async h => {
            const handle = await fileHandleStore.getHandleById(h.id)
            return handle
          })
        )
        // Filter out any null values and set the stored handles
        setStoredHandles(loadedHandles.filter((h): h is StoredHandle => h !== null))
      } catch (error) {
        console.error('Error loading handles:', error)
        setLocalErrorMessage('Failed to load repositories. Please try again.')
      }
    }

    loadHandles()
  }, [fileHandleStore, handleIds])

  const handleSelectLocalRepository = useCallback(async () => {
    try {
      setProcessingLocalAction(true)
      setLocalErrorMessage(null)

      const dirHandle: FileSystemDirectoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      })

      await addDirHandle(dirHandle)
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error accessing file system:', error)
        // Show the actual error message for empty folder or other specific errors
        const errorMessage = error.message.toLowerCase().includes('empty')
          ? error.message
          : 'Failed to access the file system. Please try again.'
        setLocalErrorMessage(errorMessage)
      }
    } finally {
      setProcessingLocalAction(false)
    }
  }, [])

  const handleRemoveLocalRepository = useCallback(async (id: string) => {
    try {
      setLocalErrorMessage(null)
      await removeDirHandle(id)
    } catch (error) {
      console.error('Failed to remove directory handle:', error)
      setLocalErrorMessage('Failed to remove repository. Please try again.')
    }
  }, [])

  const getNameStyle = useCallback((securityStatus?: string) => {
    switch (securityStatus) {
      case 'THREATS_FOUND':
        return 'text-red-600'
      case 'SCANNING':
      case 'CLEAN':
      default:
        return 'text-black/90'
    }
  }, [])

  const getSecurityStatusText = useCallback((securityStatus?: string) => {
    switch (securityStatus) {
      case 'THREATS_FOUND':
        return 'security threat detected, sync stopped'
      case 'SCANNING':
        return 'scanning...'
      default:
        return null
    }
  }, [])

  return {
    storedHandles,
    localErrorMessage,
    processingLocalAction,
    handleIds,
    handleSelectLocalRepository,
    handleRemoveLocalRepository,
    getNameStyle,
    getSecurityStatusText
  }
}
